import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation } from 'react-query';
import axiosInstance from '../utils/axiosInterceptor';
import toast from 'react-hot-toast';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  LogOut, 
  Edit3, 
  Save, 
  X,
  Briefcase,
  Award,
  Clock,
  GraduationCap
} from 'lucide-react';
import { format } from 'date-fns';

const Profile: React.FC = () => {
  const { user, logout, updateProfile } = useAuth();
  const [activeSection, setActiveSection] = useState<'personal' | 'education' | 'skills'>('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    phone: user?.profile?.phone || '',
    skills: user?.profile?.skills || [],
    experience: user?.profile?.experience || 0
  });

  // Fetch user's interview sessions
  const { data: sessions, isLoading: sessionsLoading } = useQuery(
    ['userSessions', user?.id],
    async () => {
      const response = await axiosInstance.get('/interview/sessions', {
        params: { candidateId: user?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!user?.id,
      refetchInterval: 30000 // Refetch every 30 seconds
    }
  );

  // Fetch user's reports
  const { data: reports, isLoading: reportsLoading } = useQuery(
    ['userReports', user?.id],
    async () => {
      const response = await axiosInstance.get('/admin/reports', {
        params: { candidateId: user?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!user?.id
    }
  );

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (profileData: typeof editData) => {
      await updateProfile(profileData);
    },
    {
      onSuccess: () => {
        setIsEditing(false);
        toast.success('Profile updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to update profile');
      }
    }
  );

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      firstName: user?.profile?.firstName || '',
      lastName: user?.profile?.lastName || '',
      phone: user?.profile?.phone || '',
      skills: user?.profile?.skills || [],
      experience: user?.profile?.experience || 0
    });
  };

  const handleSave = () => {
    updateProfileMutation.mutate(editData);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      firstName: user?.profile?.firstName || '',
      lastName: user?.profile?.lastName || '',
      phone: user?.profile?.phone || '',
      skills: user?.profile?.skills || [],
      experience: user?.profile?.experience || 0
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSkillsChange = (value: string) => {
    const skills = value.split(',').map(skill => skill.trim()).filter(skill => skill);
    setEditData(prev => ({
      ...prev,
      skills
    }));
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'text-green-600';
      case 'B':
        return 'text-blue-600';
      case 'C':
        return 'text-yellow-600';
      case 'D':
        return 'text-orange-600';
      case 'F':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
            <div className="flex items-center space-x-4">
              {user?.role === 'admin' && (
                <a
                  href="/admin"
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Admin Dashboard
                </a>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 w-full">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 w-full items-start">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="lg:fixed lg:top-32 lg:left-6 xl:left-[calc((100vw-80rem)/2+1.5rem)] lg:w-64 lg:z-40">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
                <nav className="space-y-1">
                  <button
                    onClick={() => setActiveSection('personal')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 text-left ${
                      activeSection === 'personal'
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <User className="h-5 w-5" />
                    <span>Personal Information</span>
                  </button>
                  <button
                    onClick={() => setActiveSection('education')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 text-left ${
                      activeSection === 'education'
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <GraduationCap className="h-5 w-5" />
                    <span>Education</span>
                  </button>
                  <button
                    onClick={() => setActiveSection('skills')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 text-left ${
                      activeSection === 'skills'
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Award className="h-5 w-5" />
                    <span>Skills</span>
                  </button>
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 flex flex-col relative">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              {/* Section Content */}
              {activeSection === 'personal' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
                    {!isEditing ? (
                      <button
                        onClick={handleEdit}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                      >
                        <Edit3 className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleSave}
                          disabled={updateProfileMutation.isLoading}
                          className="flex items-center space-x-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          <Save className="h-4 w-4" />
                          <span>Save</span>
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        {isEditing ? (
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={editData.firstName}
                              onChange={(e) => handleInputChange('firstName', e.target.value)}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                              placeholder="First name"
                            />
                            <input
                              type="text"
                              value={editData.lastName}
                              onChange={(e) => handleInputChange('lastName', e.target.value)}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                              placeholder="Last name"
                            />
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900">
                            {user?.profile?.firstName} {user?.profile?.lastName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        {isEditing ? (
                          <input
                            type="tel"
                            value={editData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder="Phone number"
                          />
                        ) : (
                          <p className="mt-1 text-sm text-gray-900">{user?.profile?.phone || 'Not provided'}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Experience (years)</label>
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            value={editData.experience}
                            onChange={(e) => handleInputChange('experience', parseInt(e.target.value) || 0)}
                            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            placeholder="Years of experience"
                          />
                        ) : (
                          <p className="mt-1 text-sm text-gray-900">{user?.profile?.experience || 0} years</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Member since</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {user?.createdAt ? format(new Date(user.createdAt), 'MMM dd, yyyy') : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {user?.lastLogin && (
                      <div className="flex items-center space-x-3">
                        <Clock className="h-5 w-5 text-gray-400" />
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700">Last login</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {format(new Date(user.lastLogin), 'MMM dd, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'education' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-semibold text-gray-900">Education</h2>
                  <p className="text-gray-600">Education information will be displayed here.</p>
                </div>
              )}

              {activeSection === 'skills' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Skills</h2>
                    {!isEditing ? (
                      <button
                        onClick={handleEdit}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                      >
                        <Edit3 className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleSave}
                          disabled={updateProfileMutation.isLoading}
                          className="flex items-center space-x-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          <Save className="h-4 w-4" />
                          <span>Save</span>
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <Award className="h-5 w-5 text-gray-400" />
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700">Skills</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.skills.join(', ')}
                          onChange={(e) => handleSkillsChange(e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          placeholder="Skills (comma separated)"
                        />
                      ) : (
                        <div className="mt-1">
                          {user?.profile?.skills && user.profile.skills.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.profile.skills.map((skill, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No skills added</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Interview Sessions and Reports */}
            <div className="mt-6 space-y-6">
              {/* Interview Sessions */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Interview Sessions</h2>
                  {sessionsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : sessions && sessions.length > 0 ? (
                    <div className="space-y-3">
                      {sessions.slice(0, 5).map((session: any) => (
                        <div key={session.sessionId} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {session.interviewId?.title || 'Interview Session'}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {session.interviewId?.role} • {session.interviewId?.domain} • {session.interviewId?.difficulty}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {format(new Date(session.createdAt), 'MMM dd, yyyy HH:mm')}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                                {session.status}
                              </span>
                              {session.status === 'completed' && (
                                <a
                                  href={`/interview?interviewId=${session.interviewId?._id}&sessionId=${session.sessionId}`}
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  View Details
                                </a>
                              )}
                            </div>
                          </div>
                          {session.progress > 0 && (
                            <div className="mt-3">
                              <div className="flex justify-between text-sm text-gray-600 mb-1">
                                <span>Progress</span>
                                <span>{session.progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full"
                                  style={{ width: `${session.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No interview sessions yet</p>
                  )}
                </div>
              </div>

              {/* Interview Reports */}
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Interview Reports</h2>
                  {reportsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : reports && reports.length > 0 ? (
                    <div className="space-y-3">
                      {reports.slice(0, 5).map((report: any) => (
                        <div key={report._id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {report.interviewId?.title || 'Interview Report'}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {report.interviewId?.role} • {report.interviewId?.domain}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {format(new Date(report.generatedAt), 'MMM dd, yyyy HH:mm')}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-lg font-bold ${getGradeColor(report.scores?.overall?.grade)}`}>
                                {report.scores?.overall?.grade || 'N/A'}
                              </span>
                              <span className="text-sm text-gray-600">
                                ({report.scores?.overall?.score?.toFixed(1) || 'N/A'}/10)
                              </span>
                            </div>
                          </div>
                          {report.aiRemarks && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">{report.aiRemarks}</p>
                            </div>
                          )}
                          <div className="mt-3 flex justify-between items-center">
                            <div className="flex space-x-4 text-sm text-gray-600">
                              <span>Communication: {report.scores?.communication?.score?.toFixed(1) || 'N/A'}/10</span>
                              <span>Technical: {report.scores?.technical?.score?.toFixed(1) || 'N/A'}/10</span>
                              <span>Confidence: {report.scores?.confidence?.score?.toFixed(1) || 'N/A'}/10</span>
                            </div>
                            {report.reportUrl && (
                              <a
                                href={report.reportUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Download Report
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No reports available yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
